import React, { type PropsWithChildren, useCallback } from 'react';
import {
  type GestureResponderEvent,
  StyleSheet,
  TouchableOpacity,
  View,
  type TouchableOpacityProps,
  type ViewProps,
} from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  interpolateColor,
  LinearTransition,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { HapticFeedbackStyle, triggerHapticFeedback } from '../../../../utils';
import { Icon, type IconName } from '../../../Icon';
import { NavbarLinkBadge } from '../../../NavbarLinkBadge';
import { BodyText } from '../../../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarLink = (props: Props) => {
  const {
    accessibilityRole = 'tab',
    accessibilityState,
    children,
    disabled,
    hasBadge = false,
    iconName,
    iconNameActive,
    isActive = false,
    onPress,
    style,
    ...restProps
  } = props;

  // ─── Derived ─────────────────────────────────────────────────────────

  const isDisabled = disabled === true;
  const activeIconName = iconNameActive ?? iconName;

  const mergedAccessibilityState = {
    ...accessibilityState,
    disabled: isDisabled,
    selected: isActive,
  };

  // ─── Animated Values ─────────────────────────────────────────────────

  const active = useDerivedValue(() => {
    return isActive ? 1 : 0;
  });

  // ─── Animated Styles ─────────────────────────────────────────────────

  const idleIconAnimatedStyles = useAnimatedStyle(() => {
    return {
      opacity: withTiming(1 - active.value, { duration: theme.transitions.short }),
    };
  });

  const activeIconAnimatedStyles = useAnimatedStyle(() => {
    return {
      opacity: withTiming(active.value, { duration: theme.transitions.short }),
    };
  });

  const labelAnimatedStyles = useAnimatedStyle(() => {
    return {
      color: interpolateColor(active.value, [0, 1], [theme.colors.CHARCOAL, theme.colors.KALE]),
    };
  });

  // ─── Handlers ───────────────────────────────────────────────────────

  const handlePress = useCallback(
    (event: GestureResponderEvent) => {
      void triggerHapticFeedback(HapticFeedbackStyle.Light);
      onPress?.(event);
    },
    [onPress],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const touchableStyles = [styles.touchable, style];
  const idleIconStyles = [styles.idleIcon, idleIconAnimatedStyles];
  const activeIconStyles = [styles.activeIcon, activeIconAnimatedStyles];
  const labelStyles = [styles.label, labelAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityRole={accessibilityRole}
      accessibilityState={mergedAccessibilityState}
      activeOpacity={NAVBAR_LINK_ACTIVE_OPACITY}
      disabled={isDisabled}
      onPress={handlePress}
      style={touchableStyles}
      {...restProps}
    >
      {isActive ? (
        <Animated.View
          entering={NAVBAR_LINK_ACTIVE_BACKGROUND_ENTERING}
          exiting={NAVBAR_LINK_ACTIVE_BACKGROUND_EXITING}
          layout={NAVBAR_LINK_ACTIVE_BACKGROUND_LAYOUT}
          pointerEvents="none"
          style={styles.activeBackground}
        />
      ) : undefined}

      <View>
        <Animated.View pointerEvents="none" style={idleIconStyles}>
          <Icon
            color={theme.colors.CHARCOAL}
            height={NAVBAR_LINK_ICON_SIZE}
            name={iconName}
            width={NAVBAR_LINK_ICON_SIZE}
          />
        </Animated.View>

        <Animated.View pointerEvents="none" style={activeIconStyles}>
          <Icon
            color={theme.colors.KALE}
            height={NAVBAR_LINK_ICON_SIZE}
            name={activeIconName}
            width={NAVBAR_LINK_ICON_SIZE}
          />
        </Animated.View>

        {hasBadge ? <NavbarLinkBadge badgeType="mobile" /> : undefined}
      </View>

      {children ? (
        <AnimatedBodyText
          sizeMatch={['12']}
          style={labelStyles}
          numberOfLines={1}
          maxFontSizeMultiplier={1.2}
        >
          {children}
        </AnimatedBodyText>
      ) : undefined}
    </TouchableOpacity>
  );
};

// ─── Animated Components ─────────────────────────────────────────────────────

const AnimatedBodyText = Animated.createAnimatedComponent(BodyText);

// ─── Constants ───────────────────────────────────────────────────────────────

const NAVBAR_LINK_ICON_SIZE = 24;
const NAVBAR_LINK_ACTIVE_OPACITY = 0.7;
const NAVBAR_LINK_ACTIVE_BACKGROUND_ENTERING = FadeIn.duration(theme.transitions.short);
const NAVBAR_LINK_ACTIVE_BACKGROUND_EXITING = FadeOut.duration(theme.transitions.short);
const NAVBAR_LINK_ACTIVE_BACKGROUND_LAYOUT = LinearTransition.duration(theme.transitions.short);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  touchable: {
    minWidth: 66,
    height: 48,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1000,
    paddingHorizontal: theme.spacing['4'],
    overflow: 'hidden',
  },
  activeBackground: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
  idleIcon: {},
  activeIcon: {
    ...StyleSheet.absoluteFill,
  },
  label: {
    textAlign: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type NavbarLinkProps = PropsWithChildren<
  {
    disabled?: boolean;
    hasBadge?: boolean;
    iconName: IconName;
    iconNameActive?: IconName;
    isActive?: boolean;
    style?: ViewProps['style'];
  } & Omit<TouchableOpacityProps, 'children' | 'disabled' | 'style'>
>;

type Props = NavbarLinkProps;
